/**
 * Dispatching teams from the interactive map.
 *
 * The demo engine already moves the fleet: it plans a route over the inferred
 * network, holds short of a runway until cleared, arrives, works and returns.
 * What it lacked was a way to send a team anywhere but an existing work
 * order. A dispatch here is a work order — raised on the spot, typed by what
 * the team is for, placed where the operator pointed — so the fleet code, the
 * work order list, the activity log and the drawing all carry it the same way
 * a maintenance job is carried. Nothing is simulated that the engine does not
 * already simulate, and the positions stay marked as such until a vehicle
 * feed is connected.
 */
import * as demo from "./generated";
import { CONTRACTOR_BOOK, CREW_TO_CONTRACTOR, FLEET, TEAMS, TEAM_COMPANY } from "./tables";

/* eslint-disable @typescript-eslint/no-explicit-any -- the demo's tables are untyped */
type Rec = any;

export type Target =
  | { kind: "wo"; id: string }
  | { kind: "asset"; id: string; name: string; E: number; N: number }
  | { kind: "point"; E: number; N: number; label?: string };

export interface TeamRow {
  k: string;
  name: string;
  kind: string;
  crew: string;
  veh: string;
  radio: string;
  speed: number;
  state: string;
  label: string;
  moveLabel: string | null;
  spd: number;
  wo: string | null;
  eta: { m: number; mins: number; to: string; road: boolean; crossings: number } | null;
  E: number;
  N: number;
  fed: boolean;
  ring: string;
  /** where on the aerodrome it is: service, apron, manoeuvring or runway */
  area: string;
}

/**
 * What each kind of team is sent for, and the priority the dispatch carries.
 * An aerodrome officer dispatching a team from the map is calling it out now,
 * whichever team it is, so every dispatch is a P1.
 */
export function purposeFor(teamK: string): { type: string; priority: "P1" | "P2" | "P3"; crew: string } {
  const t = (TEAMS as Rec[]).find((x) => x.k === teamK);
  const kind = String(t?.kind || "");
  const crew = t?.name || "Maintenance";
  if (/fire|civil defence/i.test(kind)) return { type: "Emergency response", priority: "P1", crew };
  if (/security/i.test(kind)) return { type: "Security response", priority: "P1", crew };
  if (/safety/i.test(kind)) return { type: "Safety response", priority: "P1", crew };
  if (/operations/i.test(kind)) return { type: "Airside inspection", priority: "P1", crew };
  if (/sweep/i.test(kind)) return { type: "FOD / debris", priority: "P1", crew };
  if (/lighting/i.test(kind)) return { type: "Light unit failure", priority: "P1", crew };
  if (/plant|equipment/i.test(kind)) return { type: "Equipment call-out", priority: "P1", crew };
  return { type: "Maintenance call-out", priority: "P1", crew };
}

/** The company a team answers to, by name, from the contractor book. */
export function companyFor(teamK: string): string {
  const key = (TEAM_COMPANY as Record<string, string>)[teamK];
  const c = (CONTRACTOR_BOOK as Rec[]).find((x) => x.k === key);
  return c ? String(c.name) : "Maintenance department";
}

/** The company a crew name on a work order points at, or the team's own where the crew is a team. */
export function companyForCrew(crew: string | null | undefined): string | null {
  if (!crew) return null;
  const team = (TEAMS as Rec[]).find((t) => t.name === crew);
  if (team) return companyFor(team.k);
  const key = (CREW_TO_CONTRACTOR as Record<string, string>)[crew.toLowerCase()];
  const c = key ? (CONTRACTOR_BOOK as Rec[]).find((x) => x.k === key) : null;
  return c ? String(c.name) : null;
}

/** Who is responding to a work order right now, with their ETA. */
export function respondersFor(woId: string): { k: string; name: string; state: string; mins: number | null }[] {
  return (demo.fleetSummary() as Rec[])
    .filter((t) => t.wo === woId && t.state !== "available" && t.state !== "offduty")
    .map((t) => ({ k: t.k, name: t.name, state: t.state, mins: t.eta?.mins ?? null }));
}

let seq = 0;

/** Resolve a target to a work order id, raising a dispatch order for a point or an asset. */
export function orderFor(teamK: string, target: Target, by: string, note?: string): Rec | null {
  const WOS = demo.WOS as Rec[];
  if (target.kind === "wo") return WOS.find((o) => o.id === target.id) ?? null;
  const purpose = purposeFor(teamK);
  let E: number,
    N: number,
    assetId: string | null = null,
    assetName: string;
  if (target.kind === "asset") {
    /* the pick carries the position: an inferred surface or road is not in the register, and still a place to send a team */
    E = target.E;
    N = target.N;
    assetId = target.id;
    assetName = target.name;
  } else {
    E = target.E;
    N = target.N;
    assetName = target.label ?? `${Math.round(E)} E ${Math.round(N)} N`;
  }
  const id = `DSP-${String(++seq).padStart(3, "0")}`;
  const o: Rec = {
    id,
    assetId,
    assetName,
    type: purpose.type,
    priority: purpose.priority,
    status: "open",
    notes: note?.trim() || `${purpose.type} — dispatched from the interactive map by ${by}.`,
    crew: purpose.crew,
    E,
    N,
    opened: new Date().toISOString().slice(0, 10),
    before: [],
    after: [],
    dispatch: true,
    by,
  };
  WOS.push(o);
  demo.logAction("raised a dispatch", "fleet", teamK, `${id} ${purpose.type} at ${assetName}`, []);
  return o;
}

/** Send a team. Returns the work order it was sent to. */
export function dispatch(teamK: string, target: Target, by: string, note?: string): Rec | null {
  demo.fleetSummary(); /* seeds the fleet at its bases the first time anything asks */
  const o = orderFor(teamK, target, by, note);
  if (!o) return null;
  demo.fleetAssign(teamK, o.id);
  return o;
}

/** Bring a team back to base; the job it was on stays open for someone else. */
export function recall(teamK: string): void {
  demo.fleetSummary();
  const f = (FLEET as Rec)[teamK];
  if (!f) return;
  f.state = "returning";
  f.wo = null;
  f.path = null;
  f.routeKey = null;
  f.mission = null;
  f.at = new Date().toISOString();
  const name = String((TEAMS as Rec[]).find((t) => t.k === teamK)?.name || teamK);
  /* recalled is the same order as released: off the movement area now, by the shortest way out */
  demo.logAction(
    "recalled a team",
    "fleet",
    teamK,
    inMovementArea(String(f.area || "service")) ? `${name} — vacating the movement area` : name,
    [],
  );
}

/** Task an operations team with a runway inspection under the engine's own procedure. */
export function inspectRunway(teamK: string, rwy: string): boolean {
  demo.fleetSummary();
  return demo.startInspection(teamK, rwy, undefined) != null;
}

export function teamRows(): TeamRow[] {
  return (demo.fleetSummary() as Rec[]).map((t) => ({
    k: t.k,
    name: t.name,
    kind: t.kind,
    crew: t.crew,
    veh: t.veh,
    radio: String(t.radio || ""),
    speed: t.speed,
    state: t.state,
    label: t.label,
    moveLabel: t.moveLabel ?? null,
    spd: t.spd || 0,
    wo: t.wo ?? null,
    eta: t.eta ?? null,
    E: t.E,
    N: t.N,
    fed: !!t.fed,
    ring: t.ring,
    area: String(t.area || "service"),
  }));
}

/** Start the engine's fleet clock — idempotent, safe to call on every mount. */
export const runFleet = () => demo.fleetRun();
export const stepFleet = (dt: number) => demo.fleetStep(dt);

/* ── categories and the movement area ───────────────────────────────────── */

/** The groups the dispatch bar folds the teams into, in the order a control room reaches for them. */
export const TEAM_CATEGORIES = [
  { id: "rff", label: "RFF", test: /fire/i },
  { id: "ops", label: "Airside operations", test: /airside operations/i },
  { id: "sweepers", label: "Sweepers", test: /sweep/i },
  { id: "agl", label: "AGL teams", test: /lighting|agl/i },
  { id: "maintenance", label: "Maintenance", test: /maintenance/i },
  { id: "security", label: "Security", test: /security/i },
  { id: "civil", label: "Civil Defence", test: /civil/i },
  { id: "safety", label: "Safety officers", test: /safety/i },
  { id: "equipment", label: "Equipment", test: /equipment|plant/i },
] as const;

export type TeamCategory = (typeof TEAM_CATEGORIES)[number]["id"] | "other";

export function categoryOf(kind: string): TeamCategory {
  return TEAM_CATEGORIES.find((c) => c.test.test(kind))?.id ?? "other";
}

/** The teams under each category, empty categories left out, anything unmatched under "Other". */
export function byCategory(rows: readonly TeamRow[]): { id: TeamCategory; label: string; rows: TeamRow[] }[] {
  const out: { id: TeamCategory; label: string; rows: TeamRow[] }[] = TEAM_CATEGORIES.map((c) => ({
    id: c.id,
    label: c.label,
    rows: rows.filter((r) => categoryOf(r.kind) === c.id),
  }));
  out.push({ id: "other", label: "Other", rows: rows.filter((r) => categoryOf(r.kind) === "other") });
  return out.filter((g) => g.rows.length > 0);
}

/**
 * The aircraft movement area, as a vehicle meets it: the runways and the
 * taxiways. (The apron is airside too, and a vehicle released there still
 * leaves it, but the apron's service lanes are where vehicles are meant to
 * drive.)
 */
export function inMovementArea(area: string): boolean {
  return area === "manoeuvring" || area === "runway";
}

export type MovementWhy = "working" | "inspecting" | "vacating" | "obstacle";

/**
 * Everything on the movement area and why it is there. A vehicle is allowed
 * there only for a job or an inspection; one released or recalled is on its
 * way off it; anything else — a crew with no job, equipment left behind — is
 * an obstacle, and the movement area is not clear until it is gone.
 */
export function movementArea(rows: readonly TeamRow[]): {
  clear: boolean;
  onIt: { row: TeamRow; why: MovementWhy }[];
  obstacles: number;
} {
  const onIt = rows
    .filter((r) => inMovementArea(r.area))
    .map((row) => ({
      row,
      why: (row.moveLabel?.startsWith("INSPECTING") || /inspection/i.test(row.label)
        ? "inspecting"
        : row.state === "returning" || (!row.wo && row.spd > 0)
          ? "vacating"
          : row.wo
            ? "working"
            : "obstacle") as MovementWhy,
    }));
  const obstacles = onIt.filter((x) => x.why === "obstacle").length;
  return { clear: onIt.length === 0, onIt, obstacles };
}
